import React, { useEffect, useRef, useState } from 'react';
import { Carousel, Modal, Spin } from 'antd';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight, faImages, faTimes, faImage } from '@fortawesome/free-solid-svg-icons';
import './style.scss';

const TransportPhotosModal = ({
    open,
    setOpen,
    isFetchingPhotos,
    imageUrls,
    setImageUrls,
    position,
}) => {
    const slider = useRef(null);
    const [currentIndex, setCurrentIndex] = useState(position || 0);
    const [failedImages, setFailedImages] = useState({});

    const handleImageError = (index) => {
        setFailedImages((prev) => ({
            ...prev,
            [index]: true,
        }));
    };

    const handleClose = () => {
        setOpen(false);
        setImageUrls([]);
        setCurrentIndex(0);
    };

    const handleGoTo = (index) => {
        setCurrentIndex(index);
        slider.current?.goTo(index);
    };

    useEffect(() => {
        if (open) {
            setFailedImages({});
            const pos = position || 0;
            setCurrentIndex(pos);
            if (slider.current) {
                slider.current.goTo(pos);
            }
        }
    }, [open, position]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (!open) return;
            if (e.key === 'ArrowLeft') {
                slider.current?.prev();
            } else if (e.key === 'ArrowRight') {
                slider.current?.next();
            } else if (e.key === 'Escape') {
                handleClose();
            }
        };
        // eslint-disable-next-line no-undef
        window.addEventListener('keydown', handleKeyDown);
        // eslint-disable-next-line no-undef
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [open]);

    return (
        <Modal
            centered
            width={850}
            open={open}
            footer={null}
            closable={false}
            onCancel={handleClose}
            className="transport-photos-modal">
            <div className="modal-header-custom">
                <div className="title-area">
                    <FontAwesomeIcon icon={faImages} style={{ color: '#da0812', fontSize: '18px' }} />
                    <span className="header-title">Transport Photos</span>
                    {!isFetchingPhotos && imageUrls.length > 0 && (
                        <span className="photo-counter">
                            {currentIndex + 1} / {imageUrls.length}
                        </span>
                    )}
                </div>
                <button type="button" className="close-btn" onClick={handleClose} aria-label="Close">
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>

            <div className="modal-body-content">
                {isFetchingPhotos && (
                    <div className="spinner-container">
                        <Spin size="large" />
                        <span style={{ color: '#64748b', fontSize: '0.9rem' }}>Loading photos...</span>
                    </div>
                )}
                {!isFetchingPhotos && imageUrls && imageUrls.length > 0 && (
                    <>
                        <div className="carousel-stage-container">
                            {imageUrls.length > 1 && (
                                <>
                                    <button
                                        type="button"
                                        className="nav-btn prev-btn"
                                        onClick={() => slider.current?.prev()}
                                        aria-label="Previous Photo">
                                        <FontAwesomeIcon icon={faChevronLeft} />
                                    </button>
                                    <button
                                        type="button"
                                        className="nav-btn next-btn"
                                        onClick={() => slider.current?.next()}
                                        aria-label="Next Photo">
                                        <FontAwesomeIcon icon={faChevronRight} />
                                    </button>
                                </>
                            )}
                            <Carousel
                                ref={slider}
                                autoplay={false}
                                dots={false}
                                afterChange={(current) => setCurrentIndex(current)}>
                                {imageUrls.map((imageUrl, index) => (
                                    <div key={imageUrl || index} className="slide-item">
                                        {failedImages[index] ? (
                                            <div className="image-fallback-container">
                                                <FontAwesomeIcon icon={faImage} className="fallback-icon" />
                                                <span className="fallback-text">Image not available</span>
                                            </div>
                                        ) : (
                                            <img
                                                src={imageUrl}
                                                alt={`Transport ${index + 1}`}
                                                onError={() => handleImageError(index)} />
                                        )}
                                    </div>
                                ))}
                            </Carousel>
                        </div>

                        {imageUrls.length > 1 && (
                            <div className="modal-controls-row">
                                <button
                                    type="button"
                                    className="carousel-action-btn"
                                    onClick={() => slider.current?.prev()}>
                                    <FontAwesomeIcon icon={faChevronLeft} /> Previous
                                </button>

                                <div className="thumbnail-strip-container">
                                    {imageUrls.map((url, idx) => (
                                        <div
                                            key={url || idx}
                                            className={`thumb-item ${currentIndex === idx ? 'active' : ''}`}
                                            onClick={() => handleGoTo(idx)}>
                                            {failedImages[idx] ? (
                                                <div className="thumb-fallback">
                                                    <FontAwesomeIcon icon={faImage} />
                                                </div>
                                            ) : (
                                                <img
                                                    src={url}
                                                    alt={`Thumbnail ${idx + 1}`}
                                                    onError={() => handleImageError(idx)} />
                                            )}
                                        </div>
                                    ))}
                                </div>

                                <button
                                    type="button"
                                    className="carousel-action-btn"
                                    onClick={() => slider.current?.next()}>
                                    Next <FontAwesomeIcon icon={faChevronRight} />
                                </button>
                            </div>
                        )}
                    </>
                )}
                {!isFetchingPhotos && (!imageUrls || imageUrls.length === 0) && (
                    <div className="spinner-container">
                        <span style={{ color: '#64748b' }}>No photos available</span>
                    </div>
                )}
            </div>
        </Modal>
    );
};

TransportPhotosModal.propTypes = {
    open: PropTypes.bool.isRequired,
    setOpen: PropTypes.func.isRequired,
    isFetchingPhotos: PropTypes.bool.isRequired,
    imageUrls: PropTypes.array.isRequired,
    setImageUrls: PropTypes.func.isRequired,
    position: PropTypes.number,
};

TransportPhotosModal.defaultProps = {
    position: 0,
};

export default TransportPhotosModal;
